import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from './context/AuthContext';
import { Web3Provider } from './context/Web3Context';
import CosmicBackground from './components/layout/CosmicBackground';
import Navbar from './components/layout/Navbar';
import Footer from './components/layout/Footer';

// Pages
import Landing from './pages/Landing';
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import Wallet from './pages/Wallet';
import Academy from './pages/Academy';
import Community from './pages/Community';
import Calculator from './pages/Calculator';
import Referral from './pages/Referral';
import TokenOffering from './pages/TokenOffering';
import Staking from './pages/Staking';
import LiquidityPool from './pages/LiquidityPool';
import Analytics from './pages/Analytics';
import Treasury from './pages/Treasury';

import './App.css';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// Layout Component
const Layout = ({ children }) => {
  return (
    <>
      <CosmicBackground />
      <div className="relative z-10 noise-overlay">
        <Navbar />
        <main className="min-h-screen">
          {children}
        </main>
        <Footer />
      </div>
      <Toaster 
        position="top-right"
        toastOptions={{
          style: {
            background: '#0A1628',
            border: '1px solid rgba(255,255,255,0.1)',
            color: '#fff',
          },
        }}
      />
    </>
  );
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes - Marketing Layer */}
      <Route path="/" element={<Layout><Landing /></Layout>} />
      <Route path="/token-offering" element={<Layout><TokenOffering /></Layout>} />
      <Route path="/staking" element={<Layout><Staking /></Layout>} />
      <Route path="/liquidity" element={<Layout><LiquidityPool /></Layout>} />
      <Route path="/analytics" element={<Layout><Analytics /></Layout>} />
      <Route path="/treasury" element={<Layout><Treasury /></Layout>} />
      <Route path="/academy" element={<Layout><Academy /></Layout>} />
      <Route path="/community" element={<Layout><Community /></Layout>} />
      <Route path="/calculator" element={<Layout><Calculator /></Layout>} />
      
      {/* Auth Routes */}
      <Route path="/login" element={<Layout><Login /></Layout>} />
      <Route path="/register" element={<Layout><Register /></Layout>} />
      
      {/* Protected Routes - App Layer */}
      <Route 
        path="/dashboard" 
        element={
          <Layout>
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          </Layout>
        } 
      />
      <Route 
        path="/wallet" 
        element={
          <Layout>
            <ProtectedRoute>
              <Wallet />
            </ProtectedRoute>
          </Layout>
        } 
      />
      <Route 
        path="/referral" 
        element={
          <Layout>
            <ProtectedRoute>
              <Referral />
            </ProtectedRoute>
          </Layout>
        } 
      />
      
      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Web3Provider>
          <AppRoutes />
        </Web3Provider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
